import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CookBookConnector from "../Connector/CookBookConnector";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import styled from "styled-components";
import { search } from "../../util/util";
import { errorNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import StudioService from "../Services/StudioService";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 1rem 1rem 1rem;
`;

const Div = styled.div`
	color: ${(props) => props.color};
	background: ${(props) => props.background};
	position: ${(props) => props.position};
	height: ${(props) => props.height};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-family: ${(props) => props.fontFamily};
	top: ${(props) => props.top};
	z-index: ${(props) => props.zIndex};
	max-height: ${(props) => props.maxHeight};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 310;
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	height: max-content;
	width: ${(props) => props.width};
	border-radius: 10px;
	z-index: 305;
	overflow-y: auto;
	overflow-x:hidden;
	@media (max-width: 600px) {
		width: 100%;
		height: 100%;
		overflow-y: scroll;
		border-radius: 0px;
	}
`;

const Bubble = styled.div`
	display: block;
	padding: 0.6rem 0.8rem;
	border-radius: 20px;
	color: #fff;
	background: rgba(45, 35, 66, 0.6);
`;

const Header = styled.div`
	display: flex;
	justify-content: flex-start;
	font-size: 2.3rem;
	margin: ${(props) => props.margin};
	width: 100%;
`;

const IngredientList = (props) => {
	const [query, setQuery] = useState("");
	const [searchParams, setSearchParams] = useState(["_id"]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [page, setPage] = useState(1);
	const [lastElement, setLastElement] = useState();
	const hasMore = useRef(true);
	const navigate = useNavigate();

	async function FetchIngredients() {
		setLoading(true);

		const response = await StudioService.getIngredients({ pageNumber: page, nPerPage: 10 });
		const { data, error } = response;
		if (data) {
			const { ingredientList, count } = data;
			//Add new data along with the old data
			props.ingredients.length ? props.getMoreIngredients(ingredientList) : props.getIngredients(ingredientList);

			if(count <= props.ingredients.length) {
				hasMore.current = false;
			}

			setLoading(false);
			setError(false);
		}
		if (error) {
			setLoading(false);
			setError(true);
			errorNotification("Could not update Items");
		}
	}

	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if(hasMore.current) {
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Fires everytime page changes
			FetchIngredients();
	}, [page]);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	useEffect(() => {
		return () => {
			props.getIngredients([]);
		};
	}, []);

	return (
		<Page>
			<Div background="#F2F2F2" zIndex="105" top="0" padding="1.5rem 0" alignItem="center" width="100%" jContent="space-between">
				<Header>Ingredients</Header>
			</Div>
			{!error && (
				<Table>
					<Trow head top="0">
						<Thead>Name</Thead>
						<Thead>Type</Thead>
						<Thead>Serving Temp</Thead>
						<Thead>Brand</Thead>
					</Trow>
					<Tbody>
						{search(props.ingredients, searchParams, query).map((item, index, array) =>
							index === array.length - 1 ? (
								<Trow
									innerRef={(node) => setLastElement(node)}
									hoverEffect={false}
									key={item._id}
									onClick={() => {
										navigate(`/studio/ingredient/${item._id}`);
									}}>
									<Tcol borderRadius="10px 0 0 10px">{item.name ?? "-"}</Tcol>
									<Tcol>{item?.ingredientType || "-"}</Tcol>
									<Tcol>{item?.defaultServingTemp || "-"}</Tcol>
									<Tcol borderRadius="0px 10px 10px 0px">{item?.brandId || "-"}</Tcol>
								</Trow>
							) : (
								<Trow
									key={item._id}
									hoverEffect={false}
									onClick={() => {
										navigate(`/studio/ingredient/${item._id}`);
									}}>
									<Tcol borderRadius="10px 0 0 10px">{item.name ?? "-"}</Tcol>
									<Tcol>{item?.ingredientType || "-"}</Tcol>
									<Tcol>{item?.defaultServingTemp || "-"}</Tcol>
									<Tcol borderRadius="0px 10px 10px 0px">{item?.brandId || "-"}</Tcol>
								</Trow>
							)
						)}
					</Tbody>
				</Table>
			)}

			{loading && <Loader />}
			{error && !loading && <EmptyState retry={FetchIngredients} />}

		</Page>
	);
};

export default CookBookConnector(IngredientList);
